import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	API_ENDPOINTS,
	customToast,
	DataColumnInterface,
	DataTable,
	DateFormats,
	formatDateFns,
	FormInput,
	FormInputType,
	Icon,
	Icons,
	Modal,
	PageLayout,
	Permissions,
	replaceKeyWithValue,
	route,
	StringHelpers,
	useAuthorisationContext,
	useDisabledContext,
	useLangContext,
	Variants,
} from 'carrier-fe';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';
import {
	Appliance,
	ApplianceAttributes,
} from '../../types/appliance-management';
import { SEARCH_FILTERS } from '../../Constants';
import TruncatedList from './components/TruncatedList';

/**
 * CUS-933 - the appliance catalogue listing.
 *
 * On the four columns CUS-933 specifies as single values (Gas Safe model, Gas Safe version, installation
 * type and type), plus size which it omits: all five belong to a serial prefix, not to the appliance. In
 * production they differ between prefixes of the same appliance for 27 of 48 appliances, and Vitodens
 * 100-W has 86 prefixes across 19 distinct combinations. Each cell therefore renders the de-duplicated set
 * from variant_summary, and the per-prefix detail lives on the appliance's own page.
 *
 * The row actions are View, and Restore on an archived row. Editing, archiving and importing warranty
 * options all happen from the appliance's own page, where the admin can see what they are about to change -
 * archiving and importing warranty options are both consequential enough that doing them from a table row,
 * without seeing the prefixes or options involved, invites mistakes. Restore is the exception because it is
 * the one action that only ever undoes something, and requiring a detour through the page to undo an
 * archive is friction with nothing behind it.
 *
 * Note the shared DataTable always appends format=flat, so this reads ViessmannApplianceCollection rather
 * than the nested resource. Everything below is in that collection - including variant_summary.
 */
function ApplianceManagementList() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	const [attributes, setAttributes] = useState<ApplianceAttributes | null>(
		null
	);
	const [restoring, setRestoring] = useState<Appliance | null>(null);
	const [refreshKey, setRefreshKey] = useState<string>('');

	const modelName = crud?.models?.viessmann_appliance || 'Appliance';

	const title = useMemo(
		() =>
			StringHelpers.title(
				replaceKeyWithValue(
					crud?.pages?.index?.title || ':model List',
					'model',
					modelName
				)
			),
		[crud, modelName]
	);

	useEffect(() => {
		fetchAttributes();
	}, []);

	/*
	 * The brand and appliance type filter options. They come from the API rather than being written out
	 * here because the appliance type list is the one the tickets get wrong - CUS-934 omits Oil/Gas
	 * Boiler, which is why 22 production Vitomax LW prefixes cannot be registered today.
	 */
	const fetchAttributes = async () => {
		try {
			const { data } = await axios.get(
				route(API_ENDPOINTS.GENERIC.SELECT.VIESSMANN_APPLIANCE_ATTRIBUTES)
			);

			setAttributes(data?.data ?? null);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error loading the appliance filters.',
				variant: Variants.Danger,
			});
		}
	};

	const restore = async () => {
		if (!restoring) return;

		setDisabled(true);

		try {
			const { data } = await axios.patch(
				route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.RESTORE, {
					viessmannAppliance: restoring.id,
				})
			);

			customToast({
				title: data?.message || 'Appliance restored.',
				variant: Variants.Success,
			});

			setRefreshKey(String(Date.now()));
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error restoring the appliance.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			setRestoring(null);
		}
	};

	const colMapping = (row: Appliance): DataColumnInterface[] => {
		const isArchived = !!row.deleted_at;
		const summary = row.variant_summary;

		return [
			{
				id: 'brand',
				label: StringHelpers.title(fields?.brand || 'Brand'),
				value: row.brand_display || row.brand || '-',
				sortable: true,
			},
			{
				id: 'appliance_type',
				label: StringHelpers.title(
					fields?.appliance_type || 'Appliance Type'
				),
				value: row.appliance_type_display || row.appliance_type || '-',
				sortable: true,
			},
			{
				id: 'name',
				label: StringHelpers.title(
					fields?.appliance_name || 'Appliance Name'
				),
				value: row.name || '-',
				sortable: true,
			},
			/*
			 * The five rolled-up columns. Not sortable: they are accessors over a child relation, so there
			 * is no column for the API to order by - which is why the backend deliberately leaves them and
			 * prefix_list out of allowedSorts.
			 */
			{
				id: 'gas_safe_model',
				label: StringHelpers.title(
					fields?.gas_safe_model || 'Gas Safe Model'
				),
				value: <TruncatedList values={summary?.gas_safe_models ?? []} />,
			},
			{
				id: 'gas_safe_version',
				label: StringHelpers.title(
					fields?.gas_safe_version || 'Gas Safe Version'
				),
				value: (
					<TruncatedList values={summary?.gas_safe_versions ?? []} />
				),
			},
			{
				id: 'installation_type',
				label: StringHelpers.title(
					fields?.installation_type || 'Installation Type'
				),
				value: (
					<TruncatedList values={summary?.installation_types ?? []} />
				),
			},
			{
				id: 'system_type',
				label: StringHelpers.title(fields?.type || 'Type'),
				value: <TruncatedList values={summary?.system_types ?? []} />,
			},
			{
				id: 'prefix_list',
				label: StringHelpers.title(fields?.prefixes || 'Prefixes'),
				value: (
					<TruncatedList
						values={
							row.prefix_list ? row.prefix_list.split(',') : []
						}
						limit={4}
					/>
				),
			},
			{
				id: 'updated_at',
				label: StringHelpers.title(fields?.updated_at || 'Updated'),
				value: row.updated_at
					? formatDateFns(row.updated_at, DateFormats.DATETIME_SHORT)
					: '-',
				sortable: true,
				visibleCondition: () => !isArchived,
			},
			{
				id: 'deleted_at',
				label: StringHelpers.title(fields?.archived_at || 'Archived'),
				value: row.deleted_at
					? formatDateFns(row.deleted_at, DateFormats.DATETIME_SHORT)
					: '-',
				visibleCondition: () => isArchived,
			},
			{
				id: 'actions',
				label: StringHelpers.title(fields?.actions || 'Actions'),
				value: (
					<FormInput
						type="select"
						label={StringHelpers.title(fields?.actions || 'Action')}
						placeholder={StringHelpers.title(
							fields?.select || 'Select'
						)}
						name="actions"
						value=""
						onChange={(value) => {
							if (value === 'view') {
								/*
								 * Remember where the admin was - search, both filters, archived, page and
								 * sort - so the detail page's Back button can put them back. The whole
								 * query string rather than just filter[search]: the filters only survive
								 * because the table is told to rehydrate them from the URL.
								 */
								sessionStorage.setItem(
									SEARCH_FILTERS.APPLIANCE,
									searchParams.toString()
								);
								navigate(`/appliance-management/${row.id}`);
							}

							if (value === 'restore') {
								setRestoring(row);
							}
						}}
						options={[
							/*
							 * View, plus Restore on an archived row. Edit, Archive and Import Warranty are on
							 * the appliance's own page.
							 */
							...(row.permissions?.view
								? [
										{
											label:
												crud?.select_labels?.view ||
												'View',
											value: 'view',
										},
								  ]
								: []),
							/*
							 * Gated on the row's own restore permission, which the policy only grants once
							 * the row is archived - and archived rows only appear when "View Archived" is
							 * checked. So this is already "only when viewing archived rows" without the
							 * listing having to track the checkbox itself.
							 */
							...(row.permissions?.restore
								? [
										{
											label:
												crud?.select_labels?.restore ||
												'Restore',
											value: 'restore',
										},
								  ]
								: []),
						]}
						noMargin
					/>
				),
			},
		];
	};

	/*
	 * Brand and appliance type only. There is deliberately no appliance name filter: the table's own
	 * search box sends filter[search], which the API routes to scopeSearch() - and that already matches
	 * the name, as well as the Gas Safe model, version and serial prefix. A name filter would be a strictly
	 * narrower duplicate of the box sitting next to it.
	 */
	const filters: FormInputType[] = [
		{
			name: 'brand',
			label: StringHelpers.title(fields?.brand || 'Brand'),
			type: 'searchable-select',
			value: '',
			options: attributes?.brands ?? [],
			placeholder: StringHelpers.title(fields?.select || 'select'),
			clearable: true
		},
		{
			name: 'appliance_type',
			label: StringHelpers.title(
				fields?.appliance_type || 'Appliance Type'
			),
			type: 'searchable-select',
			value: '',
			options: attributes?.appliance_types ?? [],
			placeholder: StringHelpers.title(fields?.select || 'select'),
			clearable: true
		},
	];

	const dropdownItems: DropdownItemProps[] = [
		...(hasPermissionAccess(Permissions.VIESSMANN_ADMIN_APPLIANCE_STORE)
			? [
					{
						label: StringHelpers.title(
							crud?.buttons?.create?.default || 'Create'
						),
						onClick: () => navigate('/appliance-management/create'),
						icon: <Icon icon={Icons.ADD} />,
					},
			  ]
			: []),
	];

	return (
		<PageLayout title={title || 'Appliance List'}>
			<DataTable<Appliance>
				routeEndpoint={route(
					API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.INDEX
				)}
				colMapping={colMapping}
				items={dropdownItems}
				filters={filters}
				refreshOnChange={refreshKey}
				persistFiltersInUrl
				incrementPaginationButtons
				variant={Variants.Info}
				canViewArchived
				showTotal
			/>

			<Modal
				open={!!restoring}
				onClose={() => setRestoring(null)}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.restore?.title || 'Restore :name',
						'name',
						modelName
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.restore?.submitting || 'Restoring...'
						: crud?.buttons?.restore?.default || 'Restore'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={restore}
				disabled={disabled}
				variant={Variants.Primary}
			>
				<p className="mb-0">
					{replaceKeyWithValue(
						fields?.restore_appliance_confirm ||
							'Restore :name so it appears in the appliance list again?',
						'name',
						restoring?.name || ''
					)}
				</p>
			</Modal>
		</PageLayout>
	);
}

export default ApplianceManagementList;
